/**
 * Utility functions for detecting and handling non-human-readable values
 * (GUIDs) in display contexts, and resolving human-readable labels for
 * Aras items in dropdowns.
 */

/** GUID pattern: 32 hex chars, optionally with dashes */
const GUID_REGEX = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/**
 * Check if a string value looks like a GUID (32 hex chars, with or without dashes).
 * GUIDs should never be shown to end users — prefer Display fields.
 */
export function isGuid(value: unknown): boolean {
  if (typeof value !== "string" || !value) return false;
  return GUID_REGEX.test(value.trim());
}

/**
 * Resolve a human-readable display value for an item reference field.
 * Prefers the `{field}Display` value over the raw value (which is typically a GUID).
 *
 * @param rawValue - The raw field value (usually a GUID)
 * @param displayValue - The `{field}Display` value from the API (keyed_name or name)
 * @param fallback - Fallback text if no readable value is available (default: "-")
 * @returns A human-readable string, never a raw GUID
 */
export function resolveDisplayValue(
  rawValue: unknown,
  displayValue: unknown,
  fallback = "-",
): string {
  // Prefer Display value if it's a non-empty string (even if it's an item_number like EDU-000413)
  if (displayValue && typeof displayValue === "string") {
    return displayValue;
  }

  // Fall back to raw value only if it's NOT a GUID
  if (rawValue && typeof rawValue === "string" && !isGuid(rawValue)) {
    return rawValue;
  }

  return fallback;
}

/**
 * Display fields from Aras system/identity references that should never
 * be used as the item's primary label.
 */
const systemDisplayFields = new Set([
  "currentStateDisplay",
  "siteIdDisplay",
  "createdByIdDisplay",
  "modifiedByIdDisplay",
  "ownedByIdDisplay",
  "managedByIdDisplay",
  "lockedByIdDisplay",
  "permissionIdDisplay",
]);

/**
 * Generic label resolution chain for any Aras item.
 * Tries multiple common fields in priority order, preferring
 * human-readable names over codes/numbers.
 *
 * Works for any ItemType without knowing field names in advance.
 * Used by both pre-loaded dropdown options and server-side search results.
 */
export function resolveItemLabel(
  item: Record<string, unknown>,
  configuredLabelField?: string
): string {
  // 1. Use explicitly configured label field (if provided and non-empty)
  if (configuredLabelField) {
    const configured = item[configuredLabelField] as string;
    if (configured) return configured;
  }

  // Helper: get item_number for comparison (used to skip name when it's just an auto-code)
  const itemNumber =
    (item.itemNumber as string) || (item.item_number as string) || "";

  // 2. common_name - short display name (e.g., EducationFacility)
  const commonName =
    (item.commonName as string) || (item.common_name as string);
  if (commonName) return commonName;

  // 3. Try common human-readable name fields
  // Skip name if it equals item_number (Aras auto-generates name = item_number for many types)
  const name = item.name as string;
  if (name && name !== itemNumber) return name;

  const fullName = item.fullName as string;
  if (fullName) return fullName;

  // 4. Try combining firstName + lastName (e.g., Person ItemType in Aras)
  const firstName = item.firstName as string;
  const lastName = item.lastName as string;
  if (firstName && lastName) return `${lastName} ${firstName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;

  // 5. Try contact/organization name fields (common in CRM entities like Lead)
  const contactName =
    (item.contactName as string) || (item.contact_name as string);
  if (contactName) return contactName;

  const orgName = (item.organizationName as string) || (item.orgName as string);
  if (orgName) return orgName;

  // 6. Try reference display fields (e.g., facilityIdDisplay for Lead)
  // These are human-readable names of related items, useful when the item itself has no name
  for (const [key, val] of Object.entries(item)) {
    if (
      key.endsWith("Display") &&
      !systemDisplayFields.has(key) &&
      typeof val === "string" &&
      val.trim()
    ) {
      return val.trim();
    }
  }

  // 7. keyed_name as fallback - exists on ALL Aras items
  // Skip if it equals item_number (not enriched)
  const keyedName = (item.keyedName as string) || (item.keyed_name as string);
  if (keyedName && keyedName !== itemNumber) return keyedName;

  // 8. item_number as last resort
  if (itemNumber) return itemNumber;

  // 9. Final fallback
  return (item.id as string) || "Unknown";
}
